import { useSyncExternalStore } from "react";

/**
 * Bulk email sends this tab is following.
 *
 * The Send Email dialog closes as soon as the server accepts a batch, so what
 * happens next has to be followed by something that outlives it: the tracker
 * mounted in AppLayout reads this list. It is a module store rather than React
 * state so the dialog can add to it from wherever it is rendered.
 *
 * Kept in sessionStorage so a page refresh keeps following a send in flight.
 * That is a convenience only: the batch lives on the server and finishes
 * without this tab.
 */

export interface BulkEmailJob {
  batchId: string;
  instanceId: string;
  total: number;
  /** False when the server's queue was down and this tab drives the send. */
  queued: boolean;
  /** When tracking started. Status fetched before this is stale (e.g. before a retry). */
  startedAt: number;
}

const STORAGE_KEY = "skyhire.bulkEmailJobs";
/** A batch still unfinished after this long is left to the server; stop following it. */
export const BULK_EMAIL_JOB_MAX_AGE_MS = 6 * 60 * 60 * 1000;

function isJob(value: unknown): value is BulkEmailJob {
  const job = value as BulkEmailJob;
  return (
    !!job &&
    typeof job.batchId === "string" &&
    typeof job.instanceId === "string" &&
    typeof job.total === "number" &&
    typeof job.queued === "boolean" &&
    typeof job.startedAt === "number"
  );
}

function readStored(): BulkEmailJob[] {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    const cutoff = Date.now() - BULK_EMAIL_JOB_MAX_AGE_MS;
    return parsed.filter(isJob).filter((job) => job.startedAt > cutoff);
  } catch {
    return [];
  }
}

function writeStored(): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(jobs));
  } catch {
    // Storage blocked or full: tracking still works for this page load.
  }
}

let jobs: BulkEmailJob[] = typeof window === "undefined" ? [] : readStored();
const listeners = new Set<() => void>();

function emit(): void {
  writeStored();
  listeners.forEach((listener) => listener());
}

export function trackBulkEmail(job: Omit<BulkEmailJob, "startedAt">): void {
  const next: BulkEmailJob = { ...job, startedAt: Date.now() };
  // Re-tracking a batch (a retry of its failures) restarts it.
  jobs = [...jobs.filter((j) => j.batchId !== job.batchId), next];
  emit();
}

export function untrackBulkEmail(batchId: string): void {
  if (!jobs.some((j) => j.batchId === batchId)) return;
  jobs = jobs.filter((j) => j.batchId !== batchId);
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useBulkEmailJobs(): BulkEmailJob[] {
  return useSyncExternalStore(
    subscribe,
    () => jobs,
    () => jobs,
  );
}

/** Test seam: forget every job. */
export function resetBulkEmailJobsForTests(): void {
  jobs = [];
  emit();
}

export const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** Recipients the server has finished with, whatever the outcome. */
export function doneCount(counts: { sent: number; failed: number; skipped: number }): number {
  return counts.sent + counts.failed + counts.skipped;
}

/**
 * Whether a status read belongs to this run of the job. One read before
 * tracking started (the results dialog's, before a retry) would report the
 * previous run as finished.
 */
export function isFreshFor(job: BulkEmailJob, fulfilledTimeStamp: number | undefined): boolean {
  return !!fulfilledTimeStamp && fulfilledTimeStamp >= job.startedAt;
}
